import { useState, useEffect } from 'react';
import { projectController } from '../../controllers/projectController';
import Project from '../../models/Project';
import { Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown, FolderOpen, CheckCircle, Clock, TrendingUp } from 'lucide-react';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client: '',
    projectManager: '',
    status: 'Planning',
    priority: 'Medium',
    startDate: '',
    endDate: '',
    budget: 0,
    costIncurred: 0,
    completionPercentage: 0
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchProjects(); }, []);

  useEffect(() => {
    let result = [...projects];
    if (searchQuery) {
      result = result.filter(p =>
        (p.name || p.projectName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.client || p.clientName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.projectId || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Map frontend filter to backend status
    if (filterStatus !== 'All') {
      const backendStatus = filterStatus === 'In Progress' ? 'Active' : filterStatus;
      result = result.filter(p => p.status === backendStatus);
    }
    
    if (filterPriority !== 'All') {
      result = result.filter(p => p.priority === filterPriority);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredProjects(result);
    setCurrentPage(1);
  }, [projects, searchQuery, filterStatus, filterPriority, sortField, sortDirection]);

  const fetchProjects = async () => {
    setLoading(true);
    const result = await projectController.getAll();
    if (result.success) {
      setProjects(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      description: '',
      client: '',
      projectManager: '',
      status: 'Planning',
      priority: 'Medium',
      startDate: '',
      endDate: '',
      budget: 0,
      costIncurred: 0,
      completionPercentage: 0
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    // Don't send projectManager if it's empty
    const dataToSend = { ...formData };
    if (!dataToSend.projectManager || dataToSend.projectManager.trim() === '') {
      delete dataToSend.projectManager;
    }
    
    const result = await projectController.create(dataToSend);
    if (result.success) {
      setShowAddModal(false);
      fetchProjects();
      alert('✅ Project created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name || project.projectName || '',
      description: project.description || '',
      client: project.client || project.clientName || '',
      projectManager: typeof project.projectManager === 'string' ? project.projectManager : 
                      project.projectManager?.name || '',
      status: project.status || 'Planning',
      priority: project.priority || 'Medium',
      startDate: project.startDate?.split('T')[0] || '',
      endDate: project.endDate?.split('T')[0] || '',
      budget: project.budget || 0,
      costIncurred: project.costIncurred || project.spentAmount || 0,
      completionPercentage: project.completionPercentage || project.progress || 0
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    // Don't send projectManager if it's empty
    const dataToSend = { ...formData };
    if (!dataToSend.projectManager || dataToSend.projectManager.trim() === '') {
      delete dataToSend.projectManager;
    }
    
    const result = await projectController.update(selectedProject.id || selectedProject._id, dataToSend);
    if (result.success) {
      setShowEditModal(false);
      fetchProjects();
      alert('✅ Project updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  const handleDelete = async (project) => {
    try {
      const isConfirmed = window.confirm(
        `Delete project "${project.name || project.projectName}"?`
      );
  
      if (!isConfirmed) return;
  
      const result = await projectController.delete(project.id || project._id);
  
      if (result?.success) {
        await fetchProjects();
        alert('✅ Project deleted successfully!');
      } else {
        alert(result?.message || '❌ Failed to delete project');
      }
    } catch (error) {
      console.error('Delete project error:', error);
      alert('❌ Something went wrong while deleting project');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const backendStatus = filterStatus === 'In Progress' ? 'Active' : filterStatus;
    const result = await projectController.export({ status: backendStatus !== 'All' ? backendStatus : undefined }, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projects-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Projects exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'Active').length,
    completed: projects.filter(p => p.status === 'Completed').length,
    onHold: projects.filter(p => p.status === 'On Hold').length
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', minHeight: '100px', resize: 'vertical' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
    progressBar: { width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' },
    progressFill: { height: '100%', backgroundColor: '#3b82f6', transition: 'width 0.3s' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>All Projects</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Project
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Projects</div>
            <FolderOpen size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Active</div>
            <TrendingUp size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.active}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Completed</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.completed}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>On Hold</div>
            <Clock size={24} color="#6b7280" />
          </div>
          <div style={{ ...styles.statValue, color: '#6b7280' }}>{stats.onHold}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search projects..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Planning">Planning</option>
          <option value="In Progress">In Progress</option>
          <option value="On Hold">On Hold</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        <select style={styles.select} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
          <option value="All">All Priority</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading projects...</div>
        ) : currentProjects.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No projects found. Click "Add Project" to create one.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('projectId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      ID {sortField === 'projectId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('name')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Project Name {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Client</th>
                  <th style={styles.th}>Progress</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th}>Budget</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProjects.map((project) => {
                  const displayName = project.name || project.projectName || 'Untitled';
                  const displayClient = project.client || project.clientName || 'No client';
                  const displayProgress = project.completionPercentage || project.progress || 0;
                  const displayBudget = project.budget || 0;
                  const displaySpent = project.costIncurred || project.spentAmount || 0;
                  const displayStatus = project.getDisplayStatus ? project.getDisplayStatus() : project.status;
                  const projectManagerName = typeof project.projectManager === 'string' 
                    ? project.projectManager 
                    : project.projectManager?.name || 'No manager';

                  return (
                    <tr key={project.id || project._id} style={{ cursor: 'pointer' }}>
                      <td style={styles.td}>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{project.projectId}</div>
                      </td>
                      <td style={styles.td} onClick={() => handleView(project)}>
                        <div style={{ fontWeight: '500' }}>{displayName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{projectManagerName}</div>
                      </td>
                      <td style={styles.td}>{displayClient}</td>
                      <td style={styles.td}>
                        <div style={{ marginBottom: '4px', fontSize: '12px', color: '#64748b' }}>{displayProgress}%</div>
                        <div style={styles.progressBar}>
                          <div style={{ ...styles.progressFill, width: `${displayProgress}%` }} />
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${project.getStatusColor()}20`, 
                          color: project.getStatusColor() 
                        }}>
                          {displayStatus}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${project.getPriorityColor()}20`, 
                          color: project.getPriorityColor() 
                        }}>
                          {project.priority}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ fontWeight: '500' }}>₹{displayBudget.toLocaleString()}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Spent: ₹{displaySpent.toLocaleString()}</div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleView(project); }} 
                            title="View"
                          >
                            <Eye size={18} color="#64748b" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleEdit(project); }} 
                            title="Edit"
                          >
                            <Edit2 size={18} color="#3b82f6" />
                          </button>
                          <button 
                            style={styles.actionButton} 
                            onClick={(e) => { e.stopPropagation(); handleDelete(project); }} 
                            title="Delete"
                          >
                            <Trash2 size={18} color="#ef4444" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredProjects.length)} of {filteredProjects.length} projects
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add New Project' : 'Edit Project'}</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      style={styles.input} 
                      value={formData.name} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Client Name *</label>
                    <input 
                      type="text" 
                      name="client" 
                      style={styles.input} 
                      value={formData.client} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Project Manager (Optional)</label>
                    <input 
                      type="text" 
                      name="projectManager" 
                      style={styles.input} 
                      value={formData.projectManager} 
                      onChange={handleInputChange}
                      placeholder="Leave empty if not assigned"
                    />
                    <small style={{ fontSize: '12px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                      Note: This field accepts a name for display purposes only
                    </small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select 
                      name="status" 
                      style={styles.input} 
                      value={formData.status} 
                      onChange={handleInputChange}
                    >
                      <option value="Planning">Planning</option>
                      <option value="Active">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Priority</label>
                    <select 
                      name="priority" 
                      style={styles.input} 
                      value={formData.priority} 
                      onChange={handleInputChange}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Start Date *</label>
                    <input 
                      type="date" 
                      name="startDate" 
                      style={styles.input} 
                      value={formData.startDate} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>End Date *</label>
                    <input 
                      type="date" 
                      name="endDate" 
                      style={styles.input} 
                      value={formData.endDate} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Budget (₹)</label>
                    <input 
                      type="number" 
                      name="budget" 
                      style={styles.input} 
                      value={formData.budget} 
                      onChange={handleInputChange} 
                      min="0" 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Cost Incurred (₹)</label>
                    <input 
                      type="number" 
                      name="costIncurred" 
                      style={styles.input} 
                      value={formData.costIncurred} 
                      onChange={handleInputChange} 
                      min="0" 
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Completion (%)</label>
                    <input 
                      type="number" 
                      name="completionPercentage" 
                      style={styles.input} 
                      value={formData.completionPercentage} 
                      onChange={handleInputChange} 
                      min="0" 
                      max="100" 
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description</label>
                  <textarea 
                    name="description" 
                    style={styles.textarea} 
                    value={formData.description} 
                    onChange={handleInputChange} 
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Project' : 'Update Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedProject && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Project Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedProject.projectId}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedProject.name || selectedProject.projectName}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Client Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedProject.client || selectedProject.clientName}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Project Manager</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {typeof selectedProject.projectManager === 'string' 
                      ? selectedProject.projectManager 
                      : selectedProject.projectManager?.name || 'Not assigned'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedProject.getStatusColor()}20`, 
                    color: selectedProject.getStatusColor() 
                  }}>
                    {selectedProject.getDisplayStatus ? selectedProject.getDisplayStatus() : selectedProject.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Priority</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedProject.getPriorityColor()}20`, 
                    color: selectedProject.getPriorityColor() 
                  }}>
                    {selectedProject.priority}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Start Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>End Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Budget</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    ₹{(selectedProject.budget || 0).toLocaleString()}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Cost Incurred</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    ₹{(selectedProject.costIncurred || selectedProject.spentAmount || 0).toLocaleString()}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Completion</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', marginBottom: '8px' }}>
                    {selectedProject.completionPercentage || selectedProject.progress || 0}%
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ 
                      ...styles.progressFill, 
                      width: `${selectedProject.completionPercentage || selectedProject.progress || 0}%` 
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Days Remaining</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedProject.getDaysRemaining()} days
                  </div>
                </div>
              </div>

              {selectedProject.description && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Description</div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#1e293b', 
                    padding: '12px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '8px' 
                  }}>
                    {selectedProject.description}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedProject); }}
              >
                <Edit2 size={16} /> Edit Project
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;